import { ReactNode } from 'react';
import ConstantGraph from '@/components/Icons/ConstantGraph';
import ReduceCost from '@/components/Icons/ReduceCost';
import VariationIcon from '@/components/Icons/Variation';
import {
  IItemBrandPricingComparative,
  ChartDateFilter,
} from '@/types/api/pricingsComparativeRegion';
import { formatMicroCurrency } from '@/utils';
import { percentageFormat } from '@/utils/components/quotationComparative';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

interface ICardVariationProps {
  comparative?: IItemBrandPricingComparative;
}
interface IComparativeCardProps {
  variant?: 'default' | 'success' | 'danger';
  icon: ReactNode;
  title: string;
  subtitle?: string;
  value: string;
  valueIcon?: ReactNode;
}

const ComparativeCard = (props: IComparativeCardProps) => {
  const {
    variant = 'default',
    icon,
    title,
    value,
    valueIcon,
    subtitle,
  } = props;

  const cardBackground = {
    ['default']: 'linear-gradient(90deg, #00A8F3 0%, #00D7D9 135.29%);',
    ['success']: 'linear-gradient(90deg, #54BA7B 0%, #6BF09E 156.49%)',
    ['danger']: 'linear-gradient(90deg, #E23030 0%, #F68C8C 150.79%);',
  };

  const cardPadding = {
    ['default']: 1,
    ['success']: 2,
    ['danger']: 2,
  };

  return (
    <Box
      component="div"
      sx={theme => ({
        p: 2,
        background: cardBackground[variant],
        borderRadius: '12px',
        color: 'primary.contrastText',
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 2,
        [theme.breakpoints.up('md')]: {
          py: cardPadding[variant],
          px: 0,
          flexDirection: 'column',
          textAlign: 'center',
          gap: 0,
        },
      })}
    >
      {icon}

      <Box>
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            [theme.breakpoints.up('md')]: {
              flexDirection: 'column',
              gap: 0,
            },
          })}
        >
          <Typography
            component="h3"
            variant="subtitle2"
            fontSize={16}
            sx={{ lineHeight: '1.2em' }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: '400',
                wordBreak: 'normal',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={theme => ({
            display: 'flex',
            [theme.breakpoints.up('md')]: {
              justifyContent: 'center',
              alignItems: 'center',
            },
          })}
        >
          <Typography variant="subtitle2" fontSize={18}>
            {value}
          </Typography>

          {valueIcon && valueIcon}
        </Box>
      </Box>
    </Box>
  );
};

const LowerPrice = ({ comparative }: ICardVariationProps) => {
  return (
    <ComparativeCard
      icon={<ReduceCost sx={{ fontSize: 35 }} />}
      title="Menor preço"
      value={formatMicroCurrency(comparative?.minimum_price_micros || 0)}
    />
  );
};

const AveragePrice = ({ comparative }: ICardVariationProps) => {
  return (
    <ComparativeCard
      icon={<ConstantGraph sx={{ fontSize: 35 }} />}
      title="Preço médio"
      value={formatMicroCurrency(comparative?.average_price_micros || 0)}
    />
  );
};

const Variation = ({
  comparative,
  filter,
}: ICardVariationProps & { filter: ChartDateFilter }) => {
  const comparisonHistory = comparative?.comparison_history || [];

  const prevPrice = comparisonHistory.length
    ? comparisonHistory[0].minimum_price_micros
    : 0;

  const currentPrice = comparisonHistory.length
    ? comparisonHistory[comparisonHistory.length - 1].minimum_price_micros
    : 0;

  const hasVariation = prevPrice !== currentPrice;

  const variation = hasVariation
    ? Math.abs((prevPrice - currentPrice) / prevPrice) * 100
    : 0;

  function generateLabel() {
    const symbol = `${prevPrice > currentPrice ? '-' : '+'}`;
    return hasVariation
      ? `${symbol}${percentageFormat.format(variation)}%`
      : '0%';
  }

  const ValueIcon = prevPrice > currentPrice ? TrendingDown : TrendingUp;
  const cardVariant = !hasVariation
    ? 'success'
    : prevPrice > currentPrice
    ? 'success'
    : 'danger';

  return (
    <ComparativeCard
      icon={<VariationIcon sx={{ fontSize: 35 }} />}
      title="Variação"
      subtitle={`(${filter})`}
      value={generateLabel()}
      variant={cardVariant}
      {...(hasVariation && { valueIcon: <ValueIcon /> })}
    />
  );
};

ComparativeCard.LowerPrice = LowerPrice;
ComparativeCard.AveragePrice = AveragePrice;
ComparativeCard.Variation = Variation;

export default ComparativeCard;
export type { ICardVariationProps };
